import { useState, useCallback, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import { Formik } from "formik";
import {
  AppCard,
  AppDatePicker,
  AppMultiSelect,
  AppNotificationAlert,
  AppRemixIcon,
} from "@/layout/AppComponents";
import styled from "styled-components";
import LazyLoad from "react-lazyload";
import ReactDOMServer from "react-dom/server";
import Translator from "../../../../components/helpers/Translator";
import { axiosPath } from "../../../../../routing/helpers/router";
import AppLink from "../../../../../routing/helpers/AppLink";
import { copyToClibpoard } from "../../../../../minions/string";
import {
  complaintOptions,
  statusSteps,
  statusProgress,
  statusSign,
} from "../constants/options";
import BreadcrumbHandler from "../../../../components/breadcrumb/BreadcrumbHandler";
import colors from "../../../../../constants/colors";
import _ from "lodash";

export default function CommunicationPipelineHomeAdmin() {
  const { moduleBridgeHex } = useParams();
  const formRef = useRef(null);
  const moduleData = useSelector((state) => state.currentPage?.moduleData);
  const locale = useSelector((state) => state.global.urlData.lang);
  const [reports, setReports] = useState();
  const [filterValues, setFilterValues] = useState({
    step: [],
    progress: [],
    type: [],
    from: "",
    to: "",
  });
  const [progressOptions] = useState([...statusProgress, ...statusSign]);

  const loadReports = useCallback(async () => {
    axios
      .post(
        axiosPath("api_r_user_module_communicationPipeline_admin_report_list"),
        {
          moduleBridgeHex,
          filters: filterValues,
        }
      )
      .then((r) => {
        setReports(r.data);
      })
      .catch(() => {});
  }, [moduleBridgeHex, filterValues]);

  useEffect(() => {
    loadReports();
  }, [filterValues]);

  const handleCopyClipboard = useCallback((reportHex) => {
    copyToClibpoard(reportHex);
    AppNotificationAlert({
      title: ReactDOMServer.renderToString(
        <Translator file="module-communicationPipeline" name="copied" />
      ),
      message: ReactDOMServer.renderToString(
        <Translator
          file="module-communicationPipeline"
          name="complaint-code-copied-clipboard"
        />
      ),
      type: "success",
      duration: 5000,
      position: "top-right",
    });
  }, []);

  return (
    <>
      <BreadcrumbHandler
        paths={
          moduleBridgeHex
            ? [
                {
                  name: `${moduleData?.moduleName}`,
                },
                {
                  name: ReactDOMServer.renderToString(
                    <Translator
                      file="module-communicationPipeline"
                      name="complaints"
                    />
                  ),
                },
              ]
            : [{ name: "..." }]
        }
      />

      <Container>
        <AppCard className="mb-2">
          <Formik
            validateOnChange={false}
            validateOnBlur={false}
            initialValues={filterValues}
            onSubmit={(payload) => setFilterValues(payload)}
          >
            {({
              setFieldValue,
              handleSubmit,
              values,
              errors,
              resetForm,
              initialValues,
            }) => (
              <form onSubmit={handleSubmit} ref={formRef}>
                <div className="row">
                  <div className="col-lg-12">
                    <AppMultiSelect
                      icon={<AppRemixIcon icon="apps-2" />}
                      options={complaintOptions}
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-communicationPipeline"
                          name="type"
                        />
                      )}
                      value={values?.type}
                      error={errors?.type}
                      onChange={(opt) => setFieldValue("type", opt)}
                      allowSelectAll
                      hasSearchBox
                      hasSelectOptions
                    />
                  </div>
                  <div className="col-lg-6">
                    <AppMultiSelect
                      icon={<AppRemixIcon icon="focus" />}
                      options={statusSteps}
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-communicationPipeline"
                          name="Phase"
                        />
                      )}
                      value={values?.step}
                      error={errors?.step}
                      onChange={(opt) => setFieldValue("step", opt)}
                      allowSelectAll
                      hasSearchBox
                      hasSelectOptions
                    />
                  </div>
                  <div className="col-lg-6">
                    <AppMultiSelect
                      icon={<AppRemixIcon icon="focus" filled />}
                      options={progressOptions}
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-communicationPipeline"
                          name="status"
                        />
                      )}
                      value={values?.progress}
                      error={errors?.progress}
                      onChange={(opt) => setFieldValue("progress", opt)}
                      allowSelectAll
                      hasSearchBox
                      hasSelectOptions
                    />
                  </div>
                  <div className="col-lg-6">
                    <AppDatePicker
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-communicationPipeline"
                          name="from"
                        />
                      )}
                      placeholder={ReactDOMServer.renderToString(
                        <Translator
                          file="module-communicationPipeline"
                          name="select-date"
                        />
                      )}
                      locale={locale}
                      value={values.from}
                      onChange={(opt) => setFieldValue("from", opt)}
                      showMonthDropdown
                      showYearDropdown
                      error={errors.from}
                      isClearable
                    />
                  </div>
                  <div className="col-lg-6">
                    <AppDatePicker
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-communicationPipeline"
                          name="to"
                        />
                      )}
                      placeholder={ReactDOMServer.renderToString(
                        <Translator
                          file="module-communicationPipeline"
                          name="select-date"
                        />
                      )}
                      locale={locale}
                      value={values.to}
                      onChange={(opt) => setFieldValue("to", opt)}
                      showMonthDropdown
                      showYearDropdown
                      error={errors.to}
                      isClearable
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-lg-12">
                    <button
                      type="submit"
                      className="btn btn-info btn-sm float-right"
                    >
                      <Translator
                        file="module-communicationPipeline"
                        name="filter"
                      />
                    </button>
                    {!_.isEqual(values, initialValues) && (
                      <button
                        type="submit"
                        className="btn btn-outline-danger btn-sm float-right mr-2"
                        onClick={() => resetForm()}
                      >
                        <Translator
                          file="module-communicationPipeline"
                          name="clear-filters"
                        />
                      </button>
                    )}
                  </div>
                </div>
              </form>
            )}
          </Formik>
        </AppCard>
        {reports &&
          reports.map((r) => (
            <LazyLoad key={r.hex}>
              <AppCard className="mb-2">
                <div className="d-flex align-center">
                  <AppRemixIcon
                    icon="file-copy"
                    className="pointer"
                    onClick={() => handleCopyClipboard(r.hex)}
                  />
                  <AppLink
                    path="user_module_communicationPipeline_admin_report_status"
                    params={{
                      moduleBridgeHex,
                      reportHex: r.hex,
                    }}
                    className="wl-title-md wl-title-color"
                  >
                    <span className="badge badge-info mx-2">{r?.hex}</span>
                    <span className="badge badge-dark mr-2">
                      {r?.dateStamp}
                    </span>
                    <span
                      className="badge badge-secondary mr-2"
                      style={{
                        backgroundColor:
                          colors[
                            progressOptions.find(
                              (bc) =>
                                bc?.value === r?.settings?.status?.progress
                            )?.color
                          ],
                      }}
                    >
                      {
                        statusSteps.find(
                          (ss) => ss?.value === r?.settings?.status?.step
                        )?.label
                      }
                    </span>
                    <span className="font-weight-bold">
                      {complaintOptions[r?.settings?.complaint]?.label}
                    </span>
                    {r?.settings?.description && (
                      <span>
                        {complaintOptions[r?.settings?.complaint]?.label &&
                          ": "}
                        {r?.settings?.description}
                      </span>
                    )}
                  </AppLink>
                </div>
              </AppCard>
            </LazyLoad>
          ))}
      </Container>
    </>
  );
}

const Container = styled.div`
  padding-left: 0.5rem !important;
  padding-bottom: 100px;
  h3 {
    font-size: 14px;
    font-weight: 700;
    margin: 32px 0 0 0;
  }
  .minHeight600 {
    min-height: 600px;
  }
  .user-info {
    border: 1px solid lightgray;
    padding: 24px;
  }
`;
